import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MerchantCreatorAccount, MerchantDetailsSettings } from "@unbox/api";
import type { MerchantPreferredLanguage } from "@unbox/db";
import { handleError } from "@/api/error";
import { client, useScopedMerchantClient } from "./scoped-merchant-client";

export type { MerchantCreatorAccount, MerchantDetailsSettings };

const merchantDetailsKey = (merchantId: string, workspaceId: string | null) => [
  "merchant-details-settings",
  merchantId,
  workspaceId,
];

export const useMerchantDetailsSettings = () => {
  const scope = useScopedMerchantClient();

  return useQuery<MerchantDetailsSettings>({
    queryKey: merchantDetailsKey(scope.merchantId, scope.workspaceId),
    queryFn: async () => {
      if (!scope.merchantId) throw new Error("No merchant selected");

      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["merchant-details"].$get({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["merchant-details"].$get({ param: scope.param });

      if (!res.ok) throw await handleError(res);
      return (await res.json()) as MerchantDetailsSettings;
    },
    enabled: !!scope.merchantId,
  });
};

export const useUpdateMerchantName = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["merchant-details"].$put({
            param: scope.param,
            json: { name },
          })
        : await client.merchant[":merchantId"]["merchant-details"].$put({ param: scope.param, json: { name } });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantDetailsKey(scope.merchantId, scope.workspaceId) });
      // The sidebar renders the merchant name from `useMe()`, so refresh it too or
      // the nav stays stale until the next full reload.
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useUpdateMerchantPreferredLanguage = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (preferredLanguage: MerchantPreferredLanguage) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["merchant-details"].$put({
            param: scope.param,
            json: { preferredLanguage },
          })
        : await client.merchant[":merchantId"]["merchant-details"].$put({
            param: scope.param,
            json: { preferredLanguage },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantDetailsKey(scope.merchantId, scope.workspaceId) });
    },
  });
};

export const useUploadMerchantDetailsLogo = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["merchant-details"].logo.$post({
            param: scope.param,
            form: { logo: file },
          })
        : await client.merchant[":merchantId"]["merchant-details"].logo.$post({
            param: scope.param,
            form: { logo: file },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantDetailsKey(scope.merchantId, scope.workspaceId) });
      // The sidebar renders the merchant logo from `useMe()`; keep it in sync.
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useDeleteMerchantDetailsLogo = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["merchant-details"].logo.$delete({
            param: scope.param,
          })
        : await client.merchant[":merchantId"]["merchant-details"].logo.$delete({ param: scope.param });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantDetailsKey(scope.merchantId, scope.workspaceId) });
      // The sidebar renders the merchant logo from `useMe()`; keep it in sync.
      queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
};

export const useAddMerchantCreators = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorIds: string[]) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["merchant-details"].creators.$post({
            param: scope.param,
            json: { creatorIds },
          })
        : await client.merchant[":merchantId"]["merchant-details"].creators.$post({
            param: scope.param,
            json: { creatorIds },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantDetailsKey(scope.merchantId, scope.workspaceId) });
    },
  });
};

export const useRemoveMerchantCreator = () => {
  const scope = useScopedMerchantClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (creatorId: string) => {
      const res = scope.workspaceId
        ? await client.workspace[":workspaceId"].merchant[":merchantId"]["merchant-details"].creators[
            ":creatorId"
          ].$delete({ param: { ...scope.param, creatorId } })
        : await client.merchant[":merchantId"]["merchant-details"].creators[":creatorId"].$delete({
            param: { ...scope.param, creatorId },
          });

      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: merchantDetailsKey(scope.merchantId, scope.workspaceId) });
    },
  });
};
